import React, { useState, useCallback, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  ListItem,
  ListItemText,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core'
import { useParams } from '@reach/router'
import { useSelector, useDispatch } from 'react-redux'

import Button, { BUTTON_COLOR } from '_components/button'
import TextField from '_components/textfield'
import { createMessage, updateMessage, deleteMessageAttachment } from '_modules/messages/actions'
import { userSelector } from '_modules/authentication/selectors'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import {
  getButtonsAction,
  getServiceOrder,
  getReferaCancellationFilter,
} from '_modules/service-orders/actions'
import { Preview, useConfirm, Loader } from '@refera/ui-web'
import { Attachments } from '_components/refera-ui'

import ConfirmRemoveDialog from '_components/dialogs/ConfirmRemoveDialog'
import useToggle from '_hooks/use-toggle'

import useStyles from './styles'
import { ConfidentialDataWarning } from '_components/ConfidentialDataWarning'
import { useToast } from '_hooks/use-toast'
import { useDialog } from '_hooks/use-dialog'
import { getStepStatusLog } from '_modules/budget/actions'

const obj = {
  Público: ['Qualquer pessoa poderá visualizar', 'PUBLIC'],
  'Minha empresa + Refera': ['Minha empresa e a Refera poderão visualizar', 'MY_COMPANY_CS_REFERA'],
  'Minha empresa + Intermediárias': [
    'Minha empresa e Intermediária poderão visualizar',
    'MY_COMPANY_CS_AGENCY',
  ],
  'Minha empresa': ['Apenas usuários da sua empresa poderão visualizar', 'MY_COMPANY'],
  'Apenas eu': ['Somente você poderá visualizar', 'JUST_ME'],
}

const visibleTo = Object.keys(obj).filter(label => {
  return ['Público', 'Apenas eu'].includes(label)
})

export default function SaasNewCommentModal(props) {
  const { newCommentModal, setNewCommentModal, handleUpdate } = props
  const { showToast } = useToast()
  const styles = useStyles()
  const dispatch = useDispatch()
  const { serviceOrderId } = useParams()
  const user = useSelector(userSelector)

  const [isLoading, setIsLoading] = useState(false)

  const { showDialog, closeDialog } = useDialog()

  const [commentDescription, setCommentDescription] = useState('')
  const [visibility, setVisibility] = useState('')

  const defaultPreviewState = {
    visible: false,
    selectedIndex: undefined,
  }

  const [picturesLoading, setPicturesLoading] = useState(false)
  const [editAttachments] = useToggle()
  const [dialogMessage, setDialogMessage] = useState('')
  const { isConfirmed } = useConfirm()
  const [previewState, setPreviewState] = useState(defaultPreviewState)
  const [toDelete, setToDelete] = useState([])

  const menuProps = useMemo(
    () => ({
      getContentAnchorEl: null,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center',
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'center',
      },
    }),
    []
  )

  const handleChangeDescription = e => {
    setCommentDescription(e.target.value)
  }
  const [commentAttachments, setCommentAttachment] = useState([])

  useEffect(() => {
    dispatch(getReferaCancellationFilter({ serviceOrder: serviceOrderId }))
  }, [serviceOrderId, dispatch])

  useEffect(() => {
    if (newCommentModal?.comment?.messageAttachment) {
      setCommentAttachment(
        newCommentModal?.comment?.messageAttachment.map(file => ({
          file: file?.file,
          id: file?.id,
        }))
      )
      setPicturesLoading(false)
    }
  }, [newCommentModal?.comment?.messageAttachment])

  useEffect(() => {
    if (newCommentModal?.comment) {
      const visibleToValue = obj[newCommentModal?.comment.visibleTo]
      if (visibleToValue) {
        setVisibility(visibleToValue[1])
      }
      setCommentDescription(newCommentModal?.comment.message)
    }
  }, [newCommentModal?.comment])

  const imageAttachment = useMemo(
    () =>
      commentAttachments.filter(file =>
        file instanceof File ? file.type !== 'application/pdf' : file.file.indexOf('.pdf') === -1
      ),
    [commentAttachments]
  )

  const PDFAttachment = useMemo(
    () =>
      commentAttachments.filter(file =>
        file instanceof File ? file.type === 'application/pdf' : file.file.indexOf('.pdf') !== -1
      ),
    [commentAttachments]
  )

  const validateUserEdition = useCallback(() => {
    if (newCommentModal?.comment?.senderId !== user?.id) {
      showDialog({
        type: 'warning',
        subject: 'Atenção!',
        description: 'Só o usuário que o incluiu pode realizar essa ação.',
        onApprove: () => {
          closeDialog()
        },
        labelApprove: 'Ok',
      })
      return false
    }
    return true
  }, [newCommentModal, user, showDialog, closeDialog])

  const handleSubmit = async e => {
    e.preventDefault()
    setIsLoading(true)

    const newAttachments = commentAttachments.filter(item => item?.id === undefined)

    if (newCommentModal?.comment?.id) {
      if (!validateUserEdition()) {
        setIsLoading(false)
        return
      }

      let values = {
        id: newCommentModal?.comment.id,
        message: commentDescription,
        visibleTo: visibility,
        messageAttachment: newAttachments,
      }

      if (toDelete.length > 0) {
        await Promise.allSettled(
          toDelete.map(fileID =>
            dispatch(deleteMessageAttachment(newCommentModal?.comment.id, fileID)).catch(() => {
              showToast({
                type: 'error',
                message: 'Erro ao remover anexo. Tente novamente.',
              })
            })
          )
        )
        setToDelete([]) // RESETS ARRAY TO AVOID UNECESSARY DELETE REQUESTS ON FUTURE SAVE ATTEMPTS
      }
      if (visibility) {
        values = {
          ...values,
          visibleTo: visibility,
        }
      }

      dispatch(updateMessage(values.id, values))
        .then(() => {
          dispatch(getServiceOrder(serviceOrderId))
          setNewCommentModal({ ...newCommentModal, isOpen: false })
          dispatch(getStepStatusLog(serviceOrderId))
          handleUpdate()
          setCommentDescription('')
          dispatch(getButtonsAction())
        })
        .catch(error => {
          if (error?.message?.includes('Erro ao atualizar etapa do chamado')) {
            return showToast({
              type: 'error',
              message:
                'Este chamado encontra-se em uma Etapa que não permite adicionar um comentário selecionando essa etapa.',
            })
          }

          return showToast({
            type: 'error',
            message: 'Ocorreu um erro ao salvar o comentário. Tente novamente mais tarde.',
          })
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      const values = {
        origin: 'comment',
        type: 'request',
        service_order: serviceOrderId,
        parent_message: null,
        subject: 'others',
        message: commentDescription,
        sender: user?.id,
        sender_name: user?.name,
        resolved: true,
        visibleTo: visibility,
        messageAttachment: newAttachments,
      }
      await dispatch(createMessage(values))
        .then(() => {
          handleUpdate()
          setCommentAttachment([])
          dispatch(getServiceOrder(serviceOrderId))
          dispatch(getStepStatusLog(serviceOrderId))
          dispatch(getButtonsAction())
          setCommentDescription('')
          setNewCommentModal({ ...newCommentModal, isOpen: false })
        })
        .catch(error => {
          setCommentAttachment([])
          if (error?.message?.includes('Erro ao atualizar etapa do chamado')) {
            return showToast({
              type: 'error',
              message:
                'Este chamado encontra-se em uma Etapa que não permite adicionar um comentário selecionando essa etapa.',
            })
          }
          return showToast({
            type: 'error',
            message: 'Ocorreu um erro ao salvar o comentário. Tente novamente mais tarde.',
          })
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }

  const handleCloseModal = useCallback(() => {
    setNewCommentModal({ ...newCommentModal, isOpen: false })
    setVisibility(null)
    setCommentDescription('')
    setCommentAttachment([])
    setToDelete([])
  }, [newCommentModal, setNewCommentModal])

  const handleSelectResponsible = useCallback(event => {
    setVisibility(event.target.value)
  }, [])

  const handleRemoveAttachments = useCallback(
    async (index, pdf) => {
      setDialogMessage('Você deseja deletar este item?')
      const confirmed = await isConfirmed()
      if (confirmed) {
        const currentFile = pdf ? PDFAttachment[index] : imageAttachment[index]
        if (currentFile?.id) {
          setToDelete([...toDelete, currentFile.id])
          setCommentAttachment(old => old.filter(current => current.id !== currentFile.id))
        } else {
          setCommentAttachment(old =>
            old.filter(current => current.lastModified !== currentFile?.lastModified)
          )
        }
      }
    },
    [setDialogMessage, toDelete, isConfirmed, imageAttachment, PDFAttachment, setCommentAttachment]
  )

  const [formattedImages, setFormattedImages] = useState([])

  useEffect(() => {
    const images = imageAttachment.map((item, index) =>
      item.id
        ? {
            id: item.id,
            url: item.file,
          }
        : {
            id: index,
            url: URL.createObjectURL(item),
          }
    )
    setFormattedImages(images)

    return () => {
      images.forEach(img => {
        if (img.url && img.url.startsWith('blob:')) {
          URL.revokeObjectURL(img.url)
        }
      })
    }
  }, [imageAttachment])

  const PreviewPictureComponent = useMemo(
    () => (
      <Preview
        open={previewState.visible}
        onClose={() => setPreviewState(() => defaultPreviewState)}
        selectedIndex={previewState.selectedIndex}
        images={formattedImages}
      />
    ),
    [previewState, formattedImages]
  )

  return (
    <>
      <Loader hasBackdrop open={isLoading || picturesLoading} label="Aguarde..." />
      <Dialog
        maxWidth="sm"
        fullWidth
        open={newCommentModal.isOpen}
        disableEscapeKeyDown
        disableBackdropClick
        onClose={handleCloseModal}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle className={styles.titleTight} disableTypography>
            <Typography variant="h6" className={styles.title}>
              Comentário
            </Typography>
          </DialogTitle>
          <Divider className={styles.dividerTight} />

          <DialogContent className={`${styles.dialogContent} ${styles.contentTight}`}>
            <Grid className={styles.section}>
              <Grid className={styles.column}>
                <Grid className={styles.subsection}>
                  <Grid style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography className={styles.text}>
                      Quem pode visualizar?<b className={styles.required}> *</b>
                    </Typography>
                    <FormControl required fullWidth className={styles.select}>
                      <Select
                        required
                        name="visibility"
                        id="select-visibility"
                        onChange={handleSelectResponsible}
                        MenuProps={menuProps}
                        IconComponent={ExpandMoreIcon}
                        defaultValue={
                          newCommentModal?.comment && obj[newCommentModal?.comment.visibleTo]
                            ? obj[newCommentModal?.comment.visibleTo][1]
                            : ''
                        }
                        displayEmpty
                        style={{ height: 65 }}
                      >
                        <MenuItem value="" label="">
                          <ListItem>
                            <ListItemText primary="" secondary="" />
                          </ListItem>
                        </MenuItem>
                        {visibleTo.map(label => (
                          <MenuItem
                            key={label}
                            value={obj[label][1]}
                            label={label}
                            selected={obj[label][1] === visibility}
                          >
                            <ListItem>
                              <ListItemText
                                primary={label}
                                secondary={obj[label][1] === visibility ? null : obj[label][0]}
                              />
                            </ListItem>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Typography className={styles.text}>
                  Mensagem <b className={styles.required}> *</b>
                </Typography>
                <TextField
                  className={styles.msgInput}
                  variant="outlined"
                  multiline
                  minRows={5}
                  name="comment_description"
                  id="comment_description"
                  onChange={handleChangeDescription}
                  inputProps={{ maxLength: 1500 }}
                  value={commentDescription}
                  required
                />
                <ConfidentialDataWarning style={{ marginTop: 3 }} />
              </Grid>
            </Grid>

            <Grid>
              <Grid container>
                <Typography variant="h5" component="p" className={styles.text}>
                  Anexos
                </Typography>
              </Grid>
              {picturesLoading ? (
                <Grid container justifyContent="center" alignItems="center">
                  <CircularProgress size={40} />
                </Grid>
              ) : (
                <>
                  <Attachments
                    label="Imagens e vídeos"
                    readOnly={editAttachments}
                    files={imageAttachment || []}
                    accept={{
                      'image/jpeg': [
                        '.jpeg',
                        '.png',
                        '.jpg',
                        '.bmp',
                        '.flv',
                        '.mkv',
                        '.mp4',
                        '.mov',
                      ],
                    }}
                    onDrop={files => setCommentAttachment(old => [...old, ...files])}
                    onRemoveItem={index => handleRemoveAttachments(index, false)}
                    onItemClick={index => {
                      window.open(imageAttachment[index]?.file)
                    }}
                  />
                  <Attachments
                    label="Arquivos PDF"
                    content="files"
                    readOnly={editAttachments}
                    files={PDFAttachment || []}
                    accept={{ 'application/pdf': ['.pdf'] }}
                    onDrop={files => setCommentAttachment(old => [...old, ...files])}
                    onRemoveItem={index => handleRemoveAttachments(index, true)}
                    onItemClick={index => {
                      const item = PDFAttachment[index]
                      const url = item instanceof File ? URL.createObjectURL(item) : item?.file
                      if (url) {
                        const win = window.open(url, '_blank')
                        if (item instanceof File) {
                          setTimeout(() => URL.revokeObjectURL(url), 100)
                        }
                        if (!win) {
                          showToast({
                            type: 'warning',
                            message:
                              'Pop-up bloqueado. Por favor, permita pop-ups para visualizar o PDF.',
                          })
                        }
                      }
                    }}
                  />
                </>
              )}
              {PreviewPictureComponent}
              <ConfirmRemoveDialog message={dialogMessage} />
            </Grid>
          </DialogContent>
          <DialogActions className={styles.btnWrapper}>
            <Button
              color={BUTTON_COLOR.RED}
              variant="outlined"
              className={styles.button}
              onClick={handleCloseModal}
            >
              Cancelar
            </Button>
            <Button variant="contained" color="primary" className={styles.button} type="submit">
              Salvar
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}

SaasNewCommentModal.propTypes = {
  newCommentModal: PropTypes.shape({
    isOpen: PropTypes.bool.isRequired,
    comment: PropTypes.shape({
      id: PropTypes.number,
      message: PropTypes.string,
      visibleTo: PropTypes.string,
      messageAttachment: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          file: PropTypes.string,
        })
      ),
      senderId: PropTypes.number,
    }),
    title: PropTypes.string,
  }).isRequired,
  setNewCommentModal: PropTypes.func.isRequired,
  handleUpdate: PropTypes.func.isRequired,
}
